import {DeleteIcon, EditIcon, ExternalLinkIcon} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Td,
  Text,
  Textarea,
  Tr,
  useDisclosure,
  useToast, VStack
} from "@chakra-ui/react";
import React, {useState} from "react";
import {useMutation, useQueryClient} from "react-query";

import {deleteCandidate, postCandidateSetActive, postCandidatesSetActive, updateAutomatedCandidate, updateCandidate} from "../../shared/Api";
import {errorToast, successToast} from "../../shared/helper/ToastHelper";
import {useAuthentication} from "../../shared/hooks/AuthenticationProvider";
import {Authority, CandidateStatus, Paths, SkillType} from "../../shared/model/Constants";
import {AutomatedCandidateDataDto, CandidateDataDto, CandidateDataIds, CandidateSummaryDto} from "../../shared/model/Dto";
import {adminKeys} from "../../shared/QueryKeys";
import {displayCandidateStatus, displayTime, parseDateTime} from "../../shared/Utils";
import DownloadCandidateCertificate from "./DownloadCandidateCertificate";

type CandidateSummaryItemProps = {
  candidateSummary: CandidateSummaryDto;
};

const CandidateSummaryItem = (props: CandidateSummaryItemProps) => {
  const authentication = useAuthentication();
  const isAdmin =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_ADMIN);
  const isTeacher =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_TEACHER);
  const isJudge =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_JUDGE) && !isAdmin;
  const isPublic = !isAdmin && !isTeacher && !isJudge;

  const urlParams = new URLSearchParams(window.location.search);
  const edit = urlParams.get('toggle');
  const activateCandidates = edit === 'c' || ( !isPublic  && !isJudge );

  const stackSpace= "10px";
  const stackPad= "8px";
  const maxTime = 18000;

  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const [allActive, setAllActive] = useState<boolean>( checkCandidatesActive() );

  const institution = props.candidateSummary.institution !== null ? props.candidateSummary.institution : "";
  const region = props.candidateSummary.region !== null ? props.candidateSummary.region : "";
  const institutionDisplay = institution && region ? institution+" - ("+region+")" : institution+region

  const [currentCandidateDataIds, setCurrentCandidateDataIds] = useState<CandidateDataIds>();
  const [currentCandidate, setCurrentCandidate] = useState<CandidateDataDto>();
  const [currentAutomatedCandidate, setCurrentAutomatedCandidate] = useState<AutomatedCandidateDataDto>();

  const [testIdValue, setTestIdValue] = useState<string>(""); // uses testName for automated tests
  const [levelValue, setLevelValue] = useState<string>("");
  const [statusValue, setStatusValue] = useState<string>("");
  const [activeValue, setActiveValue] = useState<boolean>(false);
  const [enabledFromValue, setEnabledFromValue] = useState<string>("");
  const [enabledToValue, setEnabledToValue] = useState<string>("");
  const [startTimeValue, setStartTimeValue] = useState<string>("");
  const [endTimeValue, setEndTimeValue] = useState<string>("");

  const [questionLimitValue, setQuestionLimitValue] = useState<number>(0);
  const [multipleAttemptsValue, setMultipleAttemptsValue] = useState<number>(0);
  const [timeLimitValue, setTimeLimitValue] = useState<number>(0);
  const [timeLeftValue, setTimeLeftValue] = useState<number>(0);
  const [conditionsValue, setConditionsValue] = useState<string>("");

  const [reportValue, setReportValue] = useState<string>("");
  const [overallValue, setOverallValue] = useState<string>("");


  function setCandidateValues(candidate: CandidateDataDto) {
    setCurrentCandidate(candidate);
    setCurrentAutomatedCandidate(undefined);

    setTestIdValue(candidate.testPublicId);
    setEnabledFromValue(candidate.enabledFrom);
    setEnabledToValue(candidate.enabledTo);
    setStartTimeValue(candidate.startTime);
    setEndTimeValue(candidate.endTime);
    setStatusValue(candidate.status);
    setLevelValue(candidate.level);
    setActiveValue(candidate.active);
    setMultipleAttemptsValue(candidate.multipleTries);

    setQuestionLimitValue(candidate.questionLimit);
    setTimeLimitValue(candidate.timeLimit);
    setTimeLeftValue(candidate.timeLeft);
    setConditionsValue(candidate.conditions);
  }

  function setAutomatedCandidateValues(candidate: AutomatedCandidateDataDto) {
    setCurrentAutomatedCandidate(candidate);
    setCurrentCandidate(undefined);

    setTestIdValue(candidate.testName || "");
    setEnabledFromValue(candidate.enabledFrom);
    setEnabledToValue(candidate.enabledTo);
    setStartTimeValue(candidate.startTime);
    setEndTimeValue(candidate.endTime);
    setStatusValue(candidate.status);
    setLevelValue(candidate.level);
    setActiveValue(candidate.active);
    setMultipleAttemptsValue(candidate.multipleTries);

    setReportValue(candidate.reportUrl);
    setOverallValue(candidate.overall);
  }

  const updateCandidateMutation = useMutation(({
                                                 candidateDataDto, newTestId
                                               }: any) =>
      updateCandidate(candidateDataDto, newTestId),
    {
      onSuccess: () => {
        if (currentCandidate) {
          currentCandidate.testPublicId = testIdValue;
          currentCandidate.testName = testIdValue;
          currentCandidate.level = levelValue;
          currentCandidate.status = CandidateStatus[statusValue as keyof typeof CandidateStatus];
          currentCandidate.active = activeValue;
          currentCandidate.enabledFrom = enabledFromValue;
          currentCandidate.enabledTo = enabledToValue;
          currentCandidate.startTime = startTimeValue;
          currentCandidate.endTime = endTimeValue;
          currentCandidate.multipleTries = multipleAttemptsValue;
          currentCandidate.questionLimit = questionLimitValue;
          currentCandidate.timeLimit = timeLimitValue;
          currentCandidate.timeLeft = timeLeftValue;
          currentCandidate.conditions = conditionsValue;
        }
        successToast(toast, "Candidato actualizado");
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });
  const updateAutomatedCandidateMutation = useMutation(({
                                                 automatedCandidateDataDto, newTestName
                                               }: any) =>
      updateAutomatedCandidate(automatedCandidateDataDto, newTestName),
    {
      onSuccess: () => {
        if (currentAutomatedCandidate) {
          //currentAutomatedCandidate.testPublicId = testPublicId;
          currentAutomatedCandidate.testName = testIdValue;
          currentAutomatedCandidate.level = levelValue;
          currentAutomatedCandidate.status = CandidateStatus[statusValue as keyof typeof CandidateStatus];
          currentAutomatedCandidate.active = activeValue;
          currentAutomatedCandidate.enabledFrom = enabledFromValue;
          currentAutomatedCandidate.enabledTo = enabledToValue;
          currentAutomatedCandidate.startTime = startTimeValue;
          currentAutomatedCandidate.endTime = endTimeValue;
          currentAutomatedCandidate.multipleTries = multipleAttemptsValue;
          currentAutomatedCandidate.reportUrl = reportValue;
          currentAutomatedCandidate.overall = overallValue;
        }
        successToast(toast, "Candidato actualizado");
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  function handleSaveClick() {
    if (currentAutomatedCandidate===undefined) {
      updateCandidateMutation.mutate({
        candidateDataDto : { username: currentCandidate?.username,

            token: currentCandidate?.token,
            skillType: currentCandidate?.skillType,
            testName: currentCandidate?.testName,
            mock: currentCandidate?.mock,

            testPublicId: currentCandidate?.testPublicId,

            enabledFrom: enabledFromValue,
            enabledTo: enabledToValue,
            startTime: startTimeValue,
            endTime: endTimeValue,
            status: statusValue,
            level: levelValue != null ? levelValue.trim() : null,
            active: activeValue,
            multipleTries: multipleAttemptsValue,

            questionLimit: questionLimitValue,
            timeLimit: timeLimitValue,
            timeLeft: timeLeftValue,
            conditions: conditionsValue != null ? conditionsValue.trim() : null
        },
        newTestId: testIdValue != null ? testIdValue.trim() : null,
      });

    } else if (currentCandidate===undefined) {
      updateAutomatedCandidateMutation.mutate({
        automatedCandidateDataDto : { username: currentAutomatedCandidate?.username,

            skillType: currentAutomatedCandidate?.skillType,
            testName: currentAutomatedCandidate?.testName,
            mock: currentAutomatedCandidate?.mock,

            testPublicId: currentAutomatedCandidate?.testPublicId,

            enabledFrom: enabledFromValue,
            enabledTo: enabledToValue,
            startTime: startTimeValue,
            endTime: endTimeValue,
            status: statusValue,
            level: levelValue != null ? levelValue.trim() : null,
            active: activeValue,
            multipleTries: multipleAttemptsValue,


            reportUrl: reportValue != null ? reportValue.trim() : null,
            overall: overallValue != null ? overallValue.trim() : null
        },
        newTestName: testIdValue != null ? testIdValue.trim() : null,
      });

    }

    onClose();
  }

  function setCandidateToDelete(testPublicId:string, token:string,
                                    testName:string, automated: boolean) {
    setCurrentCandidateDataIds({
          token: token,
          testName: testName,
          testPublicId: testPublicId,
          automated: automated
        });
  }

  function handleDeleteClick() {
    deleteCandidateMutation.mutate({
      username: props.candidateSummary.username,
      currentCandidateDataIds: currentCandidateDataIds
      });

    onCloseDelete();
  }

  const deleteCandidateMutation = useMutation(({ username, currentCandidateDataIds }: any) =>
      deleteCandidate(username, currentCandidateDataIds.token,
          currentCandidateDataIds?.testName,
          currentCandidateDataIds?.automated),
    {
      onSuccess: () => {
        if (currentCandidateDataIds) {
            if (currentCandidateDataIds.automated) {
              const index = props.candidateSummary.automatedCandidates.findIndex(c =>
                  (c.testName === currentCandidateDataIds.testName));

              if (index !== -1) {
                props.candidateSummary.automatedCandidates.splice(index, 1);
              }
            } else {
              const index = props.candidateSummary.candidates.findIndex(c =>
                  (c.testPublicId === currentCandidateDataIds.testPublicId &&
                                     c.token === currentCandidateDataIds.token  ));

              if (index !== -1) {
                props.candidateSummary.candidates.splice(index, 1);
              }
            }

          setCurrentCandidateDataIds(undefined);

        }
        successToast(toast, "Candidato eliminado");
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error eliminando candidato");
      },
    });

  function checkCandidatesActive() {
    return props.candidateSummary.candidates.every(candidate => candidate.active === true) &&
           props.candidateSummary.automatedCandidates.every(candidate => candidate.active === true);
  }

  function generateLink(testPublicId: string, token: string) {
    // return `${process.env.REACT_APP_FRONTEND_URL}${Paths.TESTS_PUBLIC_ID}`.replace(
    return `${window.location.origin}${Paths.CANDIDATE_DETAIL_PUBLIC_ID}`.replace(
      ":publicId",
      testPublicId
    ).replace(
      ":candidateToken",
      token
    ).replace(
      ":username",
      props.candidateSummary.username
    );
  }
  function goToDetail(candidateData: CandidateDataDto) {
    window.open(generateLink(candidateData.testPublicId, candidateData.token), "_blank");
    successToast(toast, "Enlace abierto en una nueva pestaña");
  }

  function goToDetailAutomated(candidateData: AutomatedCandidateDataDto) {
    window.open(candidateData.reportUrl, "_blank");
    successToast(toast, "Enlace abierto en una nueva pestaña");
  }

  function findLatest(candidates: CandidateDataDto[], skillType : SkillType): CandidateDataDto | null {
    const filtered = candidates.filter(candidate =>  candidate.skillType === skillType &&
                                     candidate.status===CandidateStatus.TEST_COMPLETE &&
                                     candidate.mock!==true  );
    return filtered.length < 1 ? null :
            filtered.reduce((latest, current) => current.startTime > latest.startTime ? current : latest);
  }

  function findLatestAutomated(candidates: CandidateDataDto[], automatedCandidates: AutomatedCandidateDataDto[],
                                skillType : SkillType): CandidateDataDto | AutomatedCandidateDataDto | null {

    const filtered = candidates.filter(candidate => candidate.skillType === skillType &&
                                     candidate.status===CandidateStatus.TEST_COMPLETE &&
                                     candidate.mock!==true);
    const latestCandidate: CandidateDataDto | null = filtered.length < 1 ? null :
          filtered.reduce((latest, current) => current.startTime > latest.startTime ? current : latest);

    const filteredAutomated = automatedCandidates.filter(candidate => candidate.skillType === skillType &&
                                     candidate.status===CandidateStatus.TEST_COMPLETE &&
                                     candidate.mock!==true);
    const latestAutomated: AutomatedCandidateDataDto | null = filteredAutomated.length < 1 ? null :
          filteredAutomated.reduce((latest, current) => current.startTime > latest.startTime ? current : latest);

    return (latestCandidate !==null && latestAutomated !==null &&
        latestCandidate.startTime > latestAutomated.startTime) || latestAutomated ===null ?
                    latestCandidate : latestAutomated;
  }

  function calculateCols() {
    const base = 4; /*<Text fontWeight="bold">Prueba</Text>
                      { (isAdmin || isJudge) && <Text>Código de revisión</Text> }
                                        <Text fontWeight="bold">Estado</Text>
                                        <Text fontWeight="bold">Inicio-Fin</Text>
                                        { !isPublic && <Text fontWeight="bold">Banda</Text> }
                                        <Text fontWeight="bold">Última pregunta</Text>
                                        { !isAdmin && activateCandidates && <Text fontWeight="bold">Habilitar</Text>}
                                        { isAdmin && <Text fontWeight="bold">Detalles</Text>}
                                        { isAdmin && <Text fontWeight="bold">Editar</Text>}
                                        */
    const notPublic = !isPublic ? 1 : 0;
    const adminCols = isAdmin ? 2 : 0;
    const judgeCols = isJudge ? 1 : 0;
    const individualActivate = !isAdmin && activateCandidates ? 1 : 0;
    const revisionCode = isAdmin || isJudge ? 1 : 0;
    return base + revisionCode + notPublic + adminCols + individualActivate + judgeCols;
  }

  const setCandidatesActive = useMutation(
    ({newActive}: any) =>
      postCandidatesSetActive( props.candidateSummary.username,
          props.candidateSummary.candidates,
          props.candidateSummary.automatedCandidates,
          newActive),
    {
      onSuccess: (data, variables) => {
        for (const candidate of props.candidateSummary.candidates) {
          candidate.active = variables.newActive;
        }
        for (const candidate of props.candidateSummary.automatedCandidates) {
          candidate.active = variables.newActive;
        }
        setAllActive(variables.newActive);

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidatos");
      },
    });

  const setCandidateActive = useMutation(
    ({publicId, candidateToken, active, isAutomated}: any) =>
      postCandidateSetActive(props.candidateSummary.username, publicId, candidateToken, active, isAutomated),
    {
      onSuccess: (data, variables) => {
        let candidate;
        if (variables.isAutomated) {
          candidate= props.candidateSummary.automatedCandidates.find(c => c.testPublicId === variables.publicId);
        } else {
          candidate= props.candidateSummary.candidates.find(c => c.testPublicId === variables.publicId &&
              c.token === variables.candidateToken  );
        }
        if (candidate)
          candidate.active = variables.active;
        setAllActive(checkCandidatesActive());

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  return (
    <>
      <Tr key={props.candidateSummary.username}>
        { ! isJudge && <Td>{props.candidateSummary.username}</Td> }
        { ! isJudge && <Td>{props.candidateSummary.name}</Td> }

        { ! isJudge && <Td>{props.candidateSummary.userGroup}</Td> }
        { ! isJudge && <Td>{institutionDisplay}</Td> }

        {   ((!isPublic  && !isJudge) || activateCandidates) &&
          <Td>
            <VStack textAlign="center">
              { !isPublic  && !isJudge &&
                <DownloadCandidateCertificate   username={props.candidateSummary.username}
                        name={props.candidateSummary.name}

                        readingC={findLatest(props.candidateSummary.candidates, SkillType.READING)}
                        listeningC={findLatest(props.candidateSummary.candidates, SkillType.LISTENING)}
                        speakingC={findLatestAutomated(props.candidateSummary.candidates,
                            props.candidateSummary.automatedCandidates, SkillType.SPEAKING)}
                        writingC={findLatestAutomated(props.candidateSummary.candidates,
                            props.candidateSummary.automatedCandidates, SkillType.WRITING)}
                />
              }
              {  activateCandidates &&
                <Button onClick={()=>setCandidatesActive.mutate({
                            newActive: !allActive
                          })}
                colorScheme={allActive ? 'red' : 'green'}
                >
                  {allActive ? 'Deshabilitar Todos' : 'Habilitar Todos'}
                </Button>
              }
            </VStack>
          </Td>
        }



        <Td> <>
            {props.candidateSummary.candidates.map((candidate) => (
              <Box
                key={candidate.testPublicId}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
              >
                <Grid templateColumns={`repeat(${calculateCols()}, 1fr)`} placeItems="center" columnGap={2}>
                  <VStack textAlign="center">
                    <Text>{ isAdmin ? candidate.testPublicId : candidate.testName} </Text>
                    <Text>({candidate.skillType})</Text>
                  </VStack>
                  { (isAdmin || isJudge) && <Text>{candidate.revisionCode}</Text> }
                  <Text>{displayCandidateStatus(candidate.status)}</Text>
                  <VStack textAlign="center">
                    <Text>{displayTime(candidate.startTime)}</Text>
                    <Text>{displayTime(candidate.endTime)}</Text>
                  </VStack>
                  { !isPublic && <Text>{candidate.level}</Text>}
                  <Text>{candidate.lastAnsweredQuestion}</Text>
                  { !isAdmin && activateCandidates &&
                      <>{candidate.status !== CandidateStatus.TEST_COMPLETE &&
                          candidate.status !== null && candidate.active &&
                                      <Button onClick={()=>setCandidateActive.mutate({
                                        publicId: candidate.testPublicId,
                                        candidateToken: candidate.token,
                                        active: false, isAutomated:false
                                      })} colorScheme='red'
                                              isLoading={setCandidateActive.isLoading} >
                                        Deshabilitar
                                      </Button>
                                    } {candidate.status !== CandidateStatus.TEST_COMPLETE &&
                                    candidate.status !== null &&
                                    !candidate.active &&
                                    <Button onClick={()=>setCandidateActive.mutate({
                                      publicId: candidate.testPublicId,
                                      candidateToken: candidate.token,
                                      active: true, isAutomated:false
                                    })} colorScheme='green'
                                            isLoading={setCandidateActive.isLoading} >
                                      Habilitar
                                    </Button>
                                    }
                      </>

                  }

                  { (isAdmin || isJudge) && <IconButton
                                    isDisabled ={candidate.status === CandidateStatus.INVITED}
                                       variant="outline"
                                       colorScheme="gray"
                                       aria-label="Abrir detalle de respuestas"
                                       size="md"
                                       icon={<ExternalLinkIcon />}
                                       marginRight="2"
                                       onClick={() => goToDetail(candidate)}
                                     />
                  }
                  { isAdmin &&  candidate.status !== null &&
                         <VStack alignSelf="center">
                             <IconButton
                             variant="outline"
                             colorScheme="gray"
                             aria-label="Editar Candidato"
                             size="md"
                             icon={<EditIcon/>}
                             marginX="auto"
                             onClick={() => {
                               setCandidateValues(candidate);
                               onOpen();
                             }}/>
                             <IconButton
                             variant="outline"
                             colorScheme="red"
                             aria-label="Eliminar Candidato"
                             size="md"
                             icon={<DeleteIcon/>}
                             marginX="auto"
                             onClick={() => {
                               setCandidateToDelete(candidate.testPublicId, candidate.token, candidate.testName, false);
                               onOpenDelete();
                             }}/>
                         </VStack>

                  }
                </Grid>
              </Box>
            ))}
          </>
        <>
            {props.candidateSummary.automatedCandidates.map((candidate) => (
              <Box
                key={candidate.testPublicId}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
              >
                <Grid templateColumns={`repeat(${calculateCols()}, 1fr)`} placeItems="center" columnGap={2}>
                  <VStack textAlign="center">
                    <Text>{candidate.testName}</Text>
                    <Text>({candidate.skillType})</Text>
                  </VStack>
                  { (isAdmin || isJudge) && <Text>{candidate.revisionCode}</Text> }
                  <Text>{displayCandidateStatus(candidate.status)}</Text>
                  <VStack textAlign="center">
                    <Text>{displayTime(candidate.startTime)}</Text>
                    <Text>{displayTime(candidate.endTime)}</Text>
                  </VStack>
                  { !isPublic && <Text>{candidate.level}</Text>}
                  <Text></Text> {/* number of answers, N/A for automated candidates */}
                  { /*isAdmin ? <Text>{candidate.reportUrl}</Text> : <Text></Text>*/}

                  { !isAdmin && activateCandidates &&
                      <>{candidate.status !== CandidateStatus.TEST_COMPLETE &&
                          candidate.status !== null && candidate.active &&
                                      <Button onClick={()=>setCandidateActive.mutate({
                                        publicId: candidate.testPublicId,
                                        candidateToken: "",
                                        active: false, isAutomated:true
                                      })} colorScheme='red'
                                              isLoading={setCandidateActive.isLoading} >
                                        Deshabilitar
                                      </Button>
                                    } {candidate.status !== CandidateStatus.TEST_COMPLETE &&
                                    candidate.status !== null &&
                                    !candidate.active &&
                                    <Button onClick={()=>setCandidateActive.mutate({
                                      publicId: candidate.testPublicId,
                                      candidateToken: "",
                                      active: true, isAutomated:true
                                    })} colorScheme='green'
                                            isLoading={setCandidateActive.isLoading} >
                                      Habilitar
                                    </Button>
                                    }
                      </>
                  }

                  { (isAdmin || isJudge) && <IconButton
                                    isDisabled ={candidate.status !== CandidateStatus.TEST_COMPLETE}
                                   variant="outline"
                                   colorScheme="gray"
                                   aria-label="Abrir detalle de respuestas"
                                   size="md"
                                   icon={<ExternalLinkIcon />}
                                   marginRight="2"
                                   onClick={() => goToDetailAutomated(candidate)}
                                 />
                  }
                  { isAdmin &&  candidate.status !== null &&
                         <VStack alignSelf="center">
                             <IconButton
                             variant="outline"
                             colorScheme="gray"
                             aria-label="Editar Candidato"
                             size="md"
                             icon={<EditIcon/>}
                             marginX="auto"
                             onClick={() => {
                               setAutomatedCandidateValues(candidate);
                               onOpen();
                             }}/>
                             <IconButton
                             variant="outline"
                             colorScheme="red"
                             aria-label="Eliminar Candidato"
                             size="md"
                             icon={<DeleteIcon/>}
                             marginX="auto"
                             onClick={() => {
                               setCandidateToDelete(candidate.testPublicId, "", candidate.testName, true);
                               onOpenDelete();
                             }}/>
                         </VStack>

                  }
                </Grid>
              </Box>
            ))}
          </>
        </Td>
      </Tr>



      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} size={"4xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar candidato</ModalHeader>
          <ModalBody>
            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Prueba</Text>
              <Textarea value={testIdValue} minW="10em" resize="none" maxW="50%" maxLength={200}
                        disabled={
                            (currentCandidate===undefined && statusValue===CandidateStatus.TEST_COMPLETE) ||
                            (currentAutomatedCandidate===undefined && statusValue!==CandidateStatus.INVITED)
                            }
                        onChange={(event)=>setTestIdValue(event.target.value)}/>

              <Text>Banda</Text>
              <Textarea value={levelValue} minW="10em" resize="none" maxW="auto" maxLength={6}
                        onChange={(event)=>setLevelValue(event.target.value)}/>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Estado</Text>
              <RadioGroup onChange={setStatusValue} value={statusValue}>
                <Stack direction='column'>
                  <Radio value={CandidateStatus.INVITED}>{displayCandidateStatus(CandidateStatus.INVITED)}</Radio>
                  <Radio value={CandidateStatus.TEST_IN_PROGRESS}>{displayCandidateStatus(CandidateStatus.TEST_IN_PROGRESS)}</Radio>
                  <Radio value={CandidateStatus.TEST_COMPLETE}>{displayCandidateStatus(CandidateStatus.TEST_COMPLETE)}</Radio>
                </Stack>
              </RadioGroup>

              <Checkbox minW="fit-content"
                        onChange={(e) => {
                          setActiveValue(e.target.checked);
                        }}
                        disabled={statusValue===CandidateStatus.TEST_COMPLETE}
                        defaultChecked={activeValue}>
                Habilitado
              </Checkbox>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Habilitado desde:</Text>
              <Input type="datetime-local" id="enabledFrom"
                     value={parseDateTime(enabledFromValue)} maxW="50%"
                     onChange={(e) => {
                       setEnabledFromValue(e.target.value);
                     }}/>
              <Text>Habilitado hasta:</Text>
              <Input type="datetime-local" id="enabledTo"
                     value={parseDateTime(enabledToValue)} maxW="50%"
                     onChange={(e) => {
                       setEnabledToValue(e.target.value);
                     }}/>
            </HStack>

            <HStack spacing={stackSpace} paddingBottom={stackPad}>
              <Text>Hora de inicio:</Text>
              <Input type="datetime-local" id="startTime"
                     value={parseDateTime(startTimeValue)} maxW="50%"
                     onChange={(e) => {
                       setStartTimeValue(e.target.value);
                     }}/>
              <Text>Hora de finalización:</Text>
              <Input type="datetime-local" id="endTime"
                     value={parseDateTime(endTimeValue)} maxW="50%"
                     onChange={(e) => {
                       setEndTimeValue(e.target.value);
                     }}/>
            </HStack>

            { (currentAutomatedCandidate===undefined) &&
              <>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Cantidad de preguntas</Text>
                  <Input type="text" maxW="auto" value={questionLimitValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) >= 0 && parseInt(value) <= 200) {
                             setQuestionLimitValue(+value);
                           }
                         }}/>
                  <Text>Mútiples intentos</Text>
                  <Input type="text" maxW="auto" value={multipleAttemptsValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) >= 0 && parseInt(value) <= 100) {
                             setMultipleAttemptsValue(+value);
                           }
                         }}/>
                </HStack>

                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Tiempo límite (segundos)</Text>
                  <Input type="text" maxW="auto" value={timeLimitValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) >= 0 && parseInt(value) <= maxTime) {
                             setTimeLimitValue(+value);
                           }
                         }}/>
                  <Text>Tiempo restante (segundos)</Text>
                  <Input type="text" maxW="auto" value={timeLeftValue}
                         onChange={(e) => {
                           const value = e.target.value.replace(/\D/,'');
                           if(parseInt(value) >= 0 && parseInt(value) <= maxTime && parseInt(value) <= timeLimitValue) {
                             setTimeLeftValue(+value);
                           }
                         }}/>
                </HStack>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Condiciones</Text>
                  <Textarea value={conditionsValue} minW="10em" resize="none" maxW="50%" maxLength={20}
                            onChange={(event) => setConditionsValue(event.target.value)}/>
                  <Text>Última respuesta: {currentCandidate?.lastAnsweredQuestion}</Text>
                </HStack>
              </>
            }

            { (currentCandidate===undefined) &&
              <>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Overall</Text>
                  <Input type="text" maxW="50%" value={overallValue}
                         onChange={(e) => {
                           const value = e.target.value;
                           if (/^(\d\.?|\d\.\d)?$/.test(value))
                             setOverallValue(value);
                         }}/>
                  <Checkbox minW="fit-content"
                            onChange={(e) => {
                              setMultipleAttemptsValue(e.target.checked ? 1 : 0);
                            }}
                            defaultChecked={multipleAttemptsValue > 0}>
                    Mútiples intentos
                  </Checkbox>
                </HStack>
                <HStack spacing={stackSpace} paddingBottom={stackPad}>
                  <Text>Reporte</Text>
                  <Textarea value={reportValue} minW="10em" resize="none" maxW="50%" maxLength={200}
                            onChange={(event) => setReportValue(event.target.value)}/>
                </HStack>
              </>
            }

          </ModalBody>
          <ModalCloseButton />
          <ModalFooter>
            <Button
              marginX={10}
              colorScheme="teal"
              type="submit"
              onClick={() => handleSaveClick()}
            >
              Guardar
            </Button>
            <Button colorScheme="teal" type="submit" onClick={()=>{
              onClose();
            }} >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal blockScrollOnMount={false} isOpen={isOpenDelete} onClose={onCloseDelete}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>¿Eliminar candidato?</ModalHeader>
          <ModalCloseButton />
          <ModalFooter>
            <Button
              marginX={10}
              colorScheme="red"
              type="submit"
              onClick={() => handleDeleteClick()}
            >
              Eliminar
            </Button>
            <Button colorScheme="teal" type="submit" onClick={()=>{
              onCloseDelete();
            }} >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


    </>
  );
};
export default CandidateSummaryItem;